/** 목록 파생 — 저작 프로젝트(운전 템플릿 제외)·퀵런 합성·워크스페이스 필터·건수(ProjectPickerPage 에서 분리, P4-c) */
import { useEffect, useMemo } from 'react';
import { useProjectStore } from '@/app/stores/projectStore';
import { useWorkspaceStore } from '@/app/stores/workspaceStore';
import { defaultWorkspaceOf, isWorkspaceId } from '@/app/workspaceFilter';
import { useDevModeStore } from '@/app/stores/devModeStore';
import { QUICKRUN_ENABLED } from '@/app/featureFlags';
import { countProjectsByWorkspace, filterProjectsByWorkspace } from '@/app/workspaceFilter';
import { isHiddenLegacyProject } from '@/app/projectVisibility';
import type { ProjectSummary } from '@/types/project';
import { QUICKRUN_PROJECT_ID, QUICKRUN_PROJECT_NAME } from '@/app/quickRunProject';


/** wsSelection: 'all' | 워크스페이스 id | null(선택 보류 — 목록 적재 전; 보일 프로젝트 없음) */
export function useProjectListing(wsSelection: string | null) {
  const { projects, loading, error, fetchProjects } = useProjectStore();
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  // 행 없는 프로젝트(퀵런)는 그 사용자의 기본 워크스페이스 행에 속한다(quickRunProject.ts)
  const defaultWorkspaceId = useMemo(() => defaultWorkspaceOf(workspaces)?.id ?? null, [workspaces]);
  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  const devMode = useDevModeStore((s) => s.devMode);
  const quickRunVisible = QUICKRUN_ENABLED || devMode;
  // 저작 화면 원천 — 빌트인 운전 플랜트(마커) 프로젝트는 운전 세계 소관
  const authoringProjects = useMemo<ProjectSummary[]>(() => projects.filter((p) => !isHiddenLegacyProject(p)), [projects]);
  const feedProjects = useMemo<ProjectSummary[]>(() => authoringProjects.filter((p) => p.id !== QUICKRUN_PROJECT_ID), [authoringProjects]);
  const workspaceCounts = useMemo(() => countProjectsByWorkspace(feedProjects), [feedProjects]);
  const displayProjects = useMemo<ProjectSummary[]>(() => {
    if (wsSelection === null) return []; // 선택 보류 — 페이지가 진행 표시를 그린다
    let base: ProjectSummary[];
    if (!quickRunVisible) base = authoringProjects.filter((p) => p.id !== QUICKRUN_PROJECT_ID);
    else if (authoringProjects.some((project) => project.id === QUICKRUN_PROJECT_ID) || !defaultWorkspaceId) base = authoringProjects;
    else {
      // 퀵런 이력 전용 기본 프로젝트는 서버에 없으면 합성한다 — 행 없는 프로젝트는 기본 워크스페이스 행에 속한다
      const now = new Date().toISOString();
      const quickRunProject: ProjectSummary = { id: QUICKRUN_PROJECT_ID, user_id: 'system', name: QUICKRUN_PROJECT_NAME, description: 'Quick Run 이력 전용 기본 프로젝트', created_at: now, updated_at: now, workspace_id: defaultWorkspaceId, model_count: 0, node_count: 0, simulation_count: 0, analysis_codes: [] };
      base = [quickRunProject, ...authoringProjects];
    }
    return filterProjectsByWorkspace(base, wsSelection);
  }, [authoringProjects, quickRunVisible, wsSelection, defaultWorkspaceId]);
  // «이미 있는 이름» = 새 프로젝트가 만들어질 워크스페이스(보고 있는 것, `all` 이면 기본 행)의 프로젝트 이름 —
  // 서버 유일성과 같은 범위(프로젝트는 워크스페이스 안, bff 0019). 사전검사 규칙은 namePolicy 하나
  const takenNames = useMemo(() => {
    const target = wsSelection === null ? null : isWorkspaceId(wsSelection) ? wsSelection : defaultWorkspaceId;
    return target ? projects.filter((p) => p.workspace_id === target).map((p) => p.name) : [];
  }, [projects, wsSelection, defaultWorkspaceId]);
  return { projects, loading, error, quickRunVisible, workspaceCounts, displayProjects, takenNames, refetch: fetchProjects };
}
